import React, { createContext, useCallback, useState } from 'react'

import { Modal } from 'antd'
import { useSelector } from 'react-redux'
import { isArray } from 'lodash'

import { selectUser } from '../../../reducers/UserReducer'
import { getIdOrNull, isObject } from '../../../utils'
import useQuota from '../../../utils/api/hooks/useQuota'
import SingleQuotaForm from './SingleQuotaForm'
import { QuotaFormModes } from './QuotaForm'
import QuotaDesiderataForm from './QuotaDesiderataForm'
import BulkQuotaForm from './BulkQuotaForm'
import { Actions } from '../../../utils/events'
import { QuotaValidators } from '../../../utils/entities/quota'
import { selectValidators } from '../../../utils/validators'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { localStorage } from '../../../utils/localStorage'
import { isSchoolUser } from '../../../utils/roles'

export const QuotaFormModalContext = createContext()

export const Types = Object.freeze({
  QUOTA: 'quota',
  DESIDERATA: 'desiderata',
  BULK: 'bulk'
})

const DEFAULT_FORM_HANDLER = { data: null, callback: null, type: Types.QUOTA }

const QuotaFormModalProvider = ({ children, mode = QuotaFormModes.REGULAR, sectors = [], sectionGroups = {}, studentPathways = [], keysToValidate = [], storageKey = null }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [loading, setLoading] = useState(false)
  const [formHandler, setFormHandler] = useState(DEFAULT_FORM_HANDLER)

  const quotaHandler = useQuota({ user, t, callback: formHandler.callback })

  const handleSubmit = useCallback(item => {
    setLoading(true)

    if (isArray(item)) {
      quotaHandler.create(item).finally(handleCloseModal)
    } else if (formHandler.type === Types.DESIDERATA && mode !== QuotaFormModes.DESIDERATA) {
      quotaHandler.acceptDesiderata(item, formHandler.data).finally(handleCloseModal)
    } else {
      quotaHandler.upsert(item, formHandler.data).finally(() => {
        handleCloseModal()

        if (
          storageKey !== null &&
          (
            getIdOrNull(formHandler.data.section) !== getIdOrNull(item.section) ||
            getIdOrNull(formHandler.data.sectionGroup) !== getIdOrNull(item.sectionGroup)
          )
        ) {
          localStorage.removeValue(storageKey, item.id)
        }
      })
    }
  }, [quotaHandler.upsert, quotaHandler.acceptDesiderata, formHandler.data])

  const handleDelete = useCallback(() => {
    setLoading(true)

    if (formHandler.type === Types.DESIDERATA && mode !== QuotaFormModes.DESIDERATA) {
      quotaHandler.refuseDesiderata(formHandler.data).finally(handleCloseModal)
    } else {
      quotaHandler.delete(formHandler.data).finally(handleCloseModal)
    }
  }, [quotaHandler.delete, quotaHandler.refuseDesiderata, formHandler.data])

  const handleCloseModal = useCallback(() => setFormHandler(DEFAULT_FORM_HANDLER), [setFormHandler])

  const action = formHandler.data?.id === -1 ? Actions.CREATE.toLocaleLowerCase() : Actions.UPDATE.toLocaleLowerCase()

  const validators = selectValidators(QuotaValidators, keysToValidate)

  return (
    <>
      <QuotaFormModalContext.Provider value={{ setFormHandler }}>
        {children}
      </QuotaFormModalContext.Provider>
      <Modal
        width={formHandler.type === Types.BULK ? 800 : 500}
        className='form-modal-without-footer'
        title={t(`quota_form_modal.title.${formHandler.type.toLowerCase()}.${action}`)}
        visible={isObject(formHandler.data)}
        onCancel={handleCloseModal}
        afterClose={() => setLoading(false)}
        destroyOnClose
        footer={null}
      >
        {(formHandler.type === Types.QUOTA || (formHandler.type === Types.DESIDERATA && mode === QuotaFormModes.DESIDERATA)) && isObject(formHandler.data) && (
          <SingleQuotaForm
            data={formHandler.data}
            mode={mode}
            sectionGroups={sectionGroups}
            sectors={sectors}
            studentPathways={studentPathways}
            dividerStyle={{ marginBottom: 5, width: '104%', transform: 'translateX(-2%)' }}
            validators={validators}
            onSubmit={handleSubmit}
            onDelete={handleDelete}
            onCancel={handleCloseModal}
            disabled={loading}
            disabledSector={isSchoolUser(user) && getIdOrNull(formHandler.data) > 0}
          />
        )}
        {formHandler.type === Types.DESIDERATA && mode !== QuotaFormModes.DESIDERATA && isObject(formHandler.data) && (
          <QuotaDesiderataForm
            data={formHandler.data}
            mode={mode}
            sectors={sectors}
            sectionGroups={sectionGroups}
            studentPathways={studentPathways}
            validators={validators}
            onSubmit={handleSubmit}
            onDelete={handleDelete}
            onCancel={handleCloseModal}
            disabled={loading}
          />
        )}
        {formHandler.type === Types.BULK && isObject(formHandler.data) && (
          <BulkQuotaForm
            data={formHandler.data}
            mode={mode}
            sectors={sectors}
            sectionGroups={sectionGroups}
            studentPathways={studentPathways}
            onSubmit={handleSubmit}
            onDelete={handleDelete}
            onCancel={handleCloseModal}
            disabled={loading}
          />
        )}
      </Modal>
    </>
  )
}

export default QuotaFormModalProvider
